/* The two pieces of tester logic that are worth being wrong about, kept PURE
 * and kept out of the DOM so they can be tested without a browser.
 *
 * **That is the point of this file existing rather than these being two
 * functions inside `tester.js`.** Nothing in this repo can drive a browser in
 * the suite, and the two worst defects of the last migration were both found by
 * the owner opening the app rather than by anything automated. Splitting the
 * logic from the rendering does not remove that gap, but it moves the part that
 * has the edge cases - overlapping groups, unset groups, zero width matches,
 * `$` references to groups that do not exist - to the side of the line that a
 * test can reach.
 */

/**
 * Split a subject into runs of text that should be drawn the same way.
 *
 * Each run is `{ text, match, group, emphasis }`: `match` is the index of the
 * match it belongs to or -1 for text outside every match, `group` is the
 * capture group number with 0 for the match itself, and `emphasis` marks the
 * span the reader is pointing at in the group table.
 *
 * **Groups are PAINTED rather than nested, and the order matters.** Group 2
 * inside group 1 is painted after it, so the inner one wins the characters they
 * share, which is what a reader expects to see. Real nested elements would need
 * the groups to nest properly and PCRE2 does not promise that: `\K` and
 * lookaround can put a group outside the match it belongs to.
 */
export function segments(subject, matches, emphasis = null) {
  const owner = new Int32Array(subject.length).fill(-1);
  const group = new Int32Array(subject.length);

  // **The emphasised span is a THIRD dimension, not a fourth colour.** It is
  // whichever group the reader is pointing at in the table, and it has to be
  // able to cover part of a run, so it breaks runs exactly as owner and group
  // do. Keeping it here rather than as a second pass over the nodes is what
  // lets it be tested without a browser, which is the whole point of this file.
  const focus = new Int8Array(subject.length);

  // One span or many: the group table points at one, and the step debugger
  // rings every match it found. Taking both keeps one code path.
  for (const span of emphasis === null || emphasis === undefined
        ? []
        : Array.isArray(emphasis) ? emphasis : [emphasis]) {
    for (let i = Math.max(0, span.start); i < Math.min(subject.length, span.end); i++) {
      focus[i] = 1;
    }
  }

  matches.forEach((groups, index) => {
    const whole = groups[0];

    if (!whole) {
      return;
    }

    for (let i = whole.start; i < whole.end; i++) {
      owner[i] = index;
      group[i] = 0;
    }

    for (let g = 1; g < groups.length; g++) {
      const span = groups[g];

      if (!span) {
        continue;
      }

      for (let i = span.start; i < span.end; i++) {
        // Only inside the match this group belongs to. A lookbehind group can
        // report a span outside it, and painting that would highlight text the
        // match does not cover.
        if (owner[i] === index) {
          group[i] = g;
        }
      }
    }
  });

  const runs = [];
  let index = 0;

  while (index < subject.length) {
    const start = index;
    const currentOwner = owner[index];
    const currentGroup = group[index];
    const currentFocus = focus[index];

    while (index < subject.length
           && owner[index] === currentOwner
           && group[index] === currentGroup
           && focus[index] === currentFocus) {
      index += 1;
    }

    runs.push({
      text: subject.slice(start, index),
      match: currentOwner,
      group: currentOwner < 0 ? 0 : currentGroup,
      emphasis: currentFocus === 1,
    });
  }

  return runs;
}

/**
 * The replace preview.
 *
 * **This is OUR substitution, not PCRE2's**, and the page says which syntax it
 * understands rather than implying it is a language's. PCRE2 has
 * `pcre2_substitute` with a richer syntax; exporting it would mean a rebuilt
 * artefact and a corpus case per feature, which is a decision rather than a
 * detail. `$0` to `$99`, and `$$` for a literal dollar.
 *
 * A reference to a group the pattern does not have is left as it was written,
 * so a typo is visible rather than silently empty. A group that exists but did
 * not participate becomes nothing, which is what every language does.
 */
export function substitute(subject, matches, replacement) {
  if (matches.length === 0 || replacement.length === 0) {
    return "";
  }

  const pieces = [];
  let read = 0;

  for (const groups of matches) {
    const whole = groups[0];

    if (!whole) {
      continue;
    }

    pieces.push(subject.slice(read, whole.start));

    pieces.push(replacement.replace(/\$(\$|\d{1,2})/g, (all, token) => {
      if (token === "$") {
        return "$";
      }

      const span = groups[Number(token)];

      if (span === undefined) {
        return all;
      }

      return span ? subject.slice(span.start, span.end) : "";
    }));

    read = whole.end;
  }

  pieces.push(subject.slice(read));

  return pieces.join("");
}
